import { usePageBreadcrumbs } from "@/components/layout/breadcrumb-context";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthProfile } from "@/hooks/use-auth-profile";
import { useRecipeBoxes } from "@/hooks/use-recipe-boxes";
import { useToast } from "@/hooks/use-toast";
import { setItemActive } from "@/lib/downgrade-service";
import type { RecipeBox } from "@/lib/types";
import { AlertCircle, Clock, Lock, ToggleLeft, ToggleRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Timestamp } from "firebase/firestore";

// ── Helpers ───────────────────────────────────────────────────────────────────

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === "object" && "toDate" in (value as object)) {
    return (value as { toDate(): Date }).toDate();
  }
  return null;
}

function formatLockCountdown(lockedUntil: Date): string {
  const now = Date.now();
  const diff = lockedUntil.getTime() - now;
  if (diff <= 0) return "unlocked";
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return `${days} day${days !== 1 ? "s" : ""} remaining`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ManageTierItemsPage() {
  const breadcrumbs = useMemo(
    () => [
      { label: "Recipe Boxes", href: "/home" },
      { label: "Manage Active Items" },
    ],
    [],
  );
  usePageBreadcrumbs(breadcrumbs);

  const { user, profile, loading: authLoading } = useAuthProfile();
  const { boxes, loading: boxesLoading } = useRecipeBoxes(user?.uid ?? null);
  const { toast } = useToast();

  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Determine lock state
  const lockedUntilDate = toDate(profile?.downgradeLockedUntil);
  const isLocked = lockedUntilDate ? lockedUntilDate.getTime() > Date.now() : false;
  const lockCountdown = lockedUntilDate && isLocked ? formatLockCountdown(lockedUntilDate) : null;

  // Split boxes by active/inactive
  const ownedBoxes = boxes.filter((b) => b.ownerUid === user?.uid);
  const activeBoxes = ownedBoxes.filter((b) => b.isActive !== false);
  const inactiveBoxes = ownedBoxes.filter((b) => b.isActive === false);

  async function handleToggleBox(box: RecipeBox, makeActive: boolean) {
    if (isLocked) return;
    setTogglingId(box.id);
    try {
      await setItemActive({ itemType: "recipeBox", itemId: box.id, isActive: makeActive });
      toast({
        title: makeActive ? "Box activated" : "Box deactivated",
        description: `"${box.title}" is now ${makeActive ? "active" : "inactive"}.`,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      toast({ title: "Could not update box", description: msg, variant: "destructive" });
    } finally {
      setTogglingId(null);
    }
  }

  if (authLoading || boxesLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!user || !profile) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
        <p>Sign in to manage your items.</p>
      </div>
    );
  }

  const accountTier = profile.accountTier ?? "free";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-headline text-2xl font-semibold">Manage Active Items</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Swap which recipe boxes are active within your{" "}
          <span className="font-medium">{accountTier === "full_pro" ? "Pro" : "Free"}</span> tier
          limits.
        </p>
      </div>

      {/* Lock notice */}
      {isLocked && lockCountdown ? (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
          <Clock className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-medium">Swap lock active — {lockCountdown}</p>
            <p className="mt-0.5 text-xs opacity-80">
              After a downgrade, items are locked for 30 days to prevent rapid cycling. You'll be
              able to swap active items once the lock expires.
            </p>
          </div>
        </div>
      ) : (
        accountTier === "free" && (
          <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-medium">You can swap active items</p>
              <p className="mt-0.5 text-xs opacity-80">
                Activating an item that would exceed your limit will automatically deactivate
                the oldest one to keep you within your free tier.
              </p>
            </div>
          </div>
        )
      )}

      {/* Active boxes */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">
          Active Boxes{" "}
          <span className="text-sm font-normal text-muted-foreground">({activeBoxes.length})</span>
        </h2>
        {activeBoxes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active boxes.</p>
        ) : (
          <div className="space-y-2">
            {activeBoxes.map((box) => (
              <BoxRow
                key={box.id}
                box={box}
                isActive
                isLocked={isLocked}
                isToggling={togglingId === box.id}
                onToggle={(b) => handleToggleBox(b, false)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Inactive boxes */}
      {inactiveBoxes.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">
            Inactive Boxes{" "}
            <span className="text-sm font-normal text-muted-foreground">
              ({inactiveBoxes.length})
            </span>
          </h2>
          <p className="text-xs text-muted-foreground">
            These boxes are hidden from collaborators. Recipes and members are preserved.
          </p>
          <div className="space-y-2">
            {inactiveBoxes.map((box) => (
              <BoxRow
                key={box.id}
                box={box}
                isActive={false}
                isLocked={isLocked}
                isToggling={togglingId === box.id}
                onToggle={(b) => handleToggleBox(b, true)}
              />
            ))}
          </div>
        </section>
      )}

      {ownedBoxes.length === 0 && (
        <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
          <p>You don't own any recipe boxes yet.</p>
        </div>
      )}
    </div>
  );
}

// ── BoxRow ────────────────────────────────────────────────────────────────────

interface BoxRowProps {
  box: RecipeBox;
  isActive: boolean;
  isLocked: boolean;
  isToggling: boolean;
  onToggle: (box: RecipeBox) => void;
}

function BoxRow({ box, isActive, isLocked, isToggling, onToggle }: BoxRowProps) {
  return (
    <Card className={`transition-opacity ${isActive ? "" : "opacity-60"}`}>
      <CardHeader className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <CardTitle className="text-sm truncate">{box.title}</CardTitle>
              {!isActive && (
                <Badge variant="secondary" className="text-xs shrink-0">
                  Inactive
                </Badge>
              )}
            </div>
            {box.description ? (
              <CardDescription className="mt-0.5 text-xs line-clamp-1">
                {box.description}
              </CardDescription>
            ) : null}
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 gap-1"
            disabled={isLocked || isToggling}
            onClick={() => onToggle(box)}
            title={isLocked ? "Swap lock is active" : isActive ? "Deactivate box" : "Activate box"}
          >
            {isLocked ? (
              <Lock className="h-4 w-4 text-muted-foreground" />
            ) : isActive ? (
              <>
                <ToggleRight className="h-4 w-4 text-green-600" />
                <span className="text-xs">Active</span>
              </>
            ) : (
              <>
                <ToggleLeft className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs">Inactive</span>
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3 pt-0">
        <p className="text-xs text-muted-foreground">{box.recipeCount} active recipes</p>
      </CardContent>
    </Card>
  );
}
